import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { useLanguage, type Language } from '@/context/LanguageContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Helmet } from 'react-helmet-async';

const schemaMsg = (language: Language, frText: string, enText: string, itText: string) => {
  if (language === 'it') return itText;
  if (language === 'en') return enText;
  return frText;
};

// Create schemas dynamically based on language
const createLoginSchema = (language: Language) => z.object({
  email: z.string().email({
    message: schemaMsg(
      language,
      'Veuillez entrer une adresse email valide',
      'Please enter a valid email address',
      'Inserisci un indirizzo email valido'
    ),
  }),
  password: z.string().min(6, {
    message: schemaMsg(
      language,
      'Le mot de passe doit contenir au moins 6 caractères',
      'Password must be at least 6 characters',
      'La password deve contenere almeno 6 caratteri'
    ),
  }),
});

const createRegisterSchema = (language: Language) => z.object({
  email: z.string().email({
    message: schemaMsg(
      language,
      'Veuillez entrer une adresse email valide',
      'Please enter a valid email address',
      'Inserisci un indirizzo email valido'
    ),
  }),
  password: z.string().min(6, {
    message: schemaMsg(
      language,
      'Le mot de passe doit contenir au moins 6 caractères',
      'Password must be at least 6 characters',
      'La password deve contenere almeno 6 caratteri'
    ),
  }),
  confirmPassword: z.string().min(6, {
    message: schemaMsg(
      language,
      'Veuillez confirmer votre mot de passe',
      'Please confirm your password',
      'Conferma la tua password'
    ),
  }),
  fullName: z.string().min(2, {
    message: schemaMsg(
      language,
      'Le nom complet est requis',
      'Full name is required',
      'Il nome completo è obbligatorio'
    ),
  }),
  phoneNumber: z.string().min(5, {
    message: schemaMsg(
      language,
      'Le numéro de téléphone est requis',
      'Phone number is required',
      'Il numero di telefono è obbligatorio'
    ),
  }),
  address: z.string().min(5, {
    message: schemaMsg(
      language,
      "L'adresse est requise",
      'Address is required',
      "L'indirizzo è obbligatorio"
    ),
  }),
  city: z.string().min(2, {
    message: schemaMsg(
      language,
      'La ville est requise',
      'City is required',
      'La città è obbligatoria'
    ),
  }),
  zipCode: z.string().min(4, {
    message: schemaMsg(
      language,
      'Le code postal est requis',
      'Zip code is required',
      'Il codice postale è obbligatorio'
    ),
  }),
  country: z.string().min(2, {
    message: schemaMsg(
      language,
      'Le pays est requis',
      'Country is required',
      'Il paese è obbligatorio'
    ),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: schemaMsg(
    language,
    'Les mots de passe ne correspondent pas',
    "Passwords don't match",
    'Le password non corrispondono'
  ),
  path: ['confirmPassword'],
});

const Auth = () => {
  const { language, tl } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>('login');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { signIn, signUp, signInWithGoogle, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Create schemas based on current language (memoized to avoid recreating on every render)
  const loginSchema = useMemo(() => createLoginSchema(language), [language]);
  const registerSchema = useMemo(() => createRegisterSchema(language), [language]);

  type LoginFormValues = z.infer<typeof loginSchema>;
  type RegisterFormValues = z.infer<typeof registerSchema>;

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Login form
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Registration form
  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      phoneNumber: '',
      address: '',
      city: '',
      zipCode: '',
      country: '',
    },
  });

  // Clear form errors when language changes (schemas will be re-evaluated on next validation)
  useEffect(() => {
    loginForm.clearErrors();
    registerForm.clearErrors();
  }, [language]);

  // Handle login form submission
  const onLoginSubmit = async (values: LoginFormValues) => {
    setAuthError(null);
    try {
      await signIn(values.email, values.password);
      navigate('/');
    } catch (error: any) {
      setAuthError(
        error.message ||
          tl(
            'Échec de la connexion. Veuillez vérifier vos identifiants.',
            'Failed to sign in. Please check your credentials.',
            'Accesso non riuscito. Verifica le tue credenziali.'
          )
      );
    }
  };

  // Handle registration form submission
  const onRegisterSubmit = async (values: RegisterFormValues) => {
    setAuthError(null);
    try {
      // Extract and format user data for registration
      const { confirmPassword, ...userData } = values;

      // Set user metadata with additional fields
      const options = {
        data: {
          full_name: values.fullName,
          phone_number: values.phoneNumber,
          address: values.address,
          city: values.city,
          zip_code: values.zipCode,
          country: values.country,
        },
      };

      await signUp(values.email, values.password, options);
      toast({
        title: tl('Inscription réussie', 'Registration successful', 'Registrazione riuscita'),
        description: tl(
          'Votre compte a été créé. Veuillez vérifier votre email pour confirmer votre compte.',
          'Your account has been created. Please check your email to verify your account.',
          'Il tuo account è stato creato. Controlla la tua email per confermare il tuo account.'
        ),
      });
      setActiveTab('login');
    } catch (error: any) {
      setAuthError(
        error.message ||
          tl(
            "Échec de l'inscription. Veuillez réessayer.",
            'Failed to sign up. Please try again.',
            'Registrazione non riuscita. Riprova.'
          )
      );
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 bg-white">
      <Helmet>
        <title>
          {tl(
            'Connexion ou Inscription | Meubles Karim',
            'Sign In or Register | Meubles Karim',
            'Accesso o Registrazione | Meubles Karim'
          )}
        </title>
        <meta
          name="description"
          content={tl(
            'Connectez-vous à votre compte ou créez-en un nouveau pour commencer à magasiner chez Meubles Karim.',
            'Sign in to your account or create a new one to start shopping at Meubles Karim.',
            'Accedi al tuo account o creane uno nuovo per iniziare a fare shopping da Meubles Karim.'
          )}
        />
      </Helmet>

      <div className="max-w-md mx-auto">
        <Tabs defaultValue="login" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">{tl('Connexion', 'Sign In', 'Accesso')}</TabsTrigger>
            <TabsTrigger value="register">{tl("S'inscrire", 'Register', 'Registrati')}</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <Card className="shadow-xl border-0">
              <CardHeader>
                <CardTitle>{tl('Connexion', 'Sign In', 'Accesso')}</CardTitle>
                <CardDescription>
                  {tl(
                    'Entrez vos identifiants pour accéder à votre compte',
                    'Enter your credentials to access your account',
                    'Inserisci le tue credenziali per accedere al tuo account'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Google Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mb-4"
                  onClick={() => signInWithGoogle()}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {tl('Continuer avec Google', 'Continue with Google', 'Continua con Google')}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">
                      {tl('ou', 'or', 'o')}
                    </span>
                  </div>
                </div>

                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Email', 'Email', 'Email')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tl(
                                'votre.email@exemple.com',
                                'your.email@example.com',
                                'tua.email@esempio.com'
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Mot de passe', 'Password', 'Password')}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-10 w-10 px-3 py-2"
                                onClick={togglePasswordVisibility}
                              >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {authError && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertTitle>{tl('Erreur', 'Error', 'Errore')}</AlertTitle>
                        <AlertDescription>{authError}</AlertDescription>
                      </Alert>
                    )}

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {tl('Connexion...', 'Signing in...', 'Accesso...')}
                        </>
                      ) : (
                        tl('Se connecter', 'Sign In', 'Accedi')
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
              <CardFooter>
                <p className="text-sm text-center w-full text-muted-foreground">
                  {tl("Vous n'avez pas de compte ?", "Don't have an account?", 'Non hai un account?')}{' '}
                  <Button
                    variant="link"
                    className="p-0 h-auto font-normal"
                    onClick={() => setActiveTab('register')}
                  >
                    {tl("S'inscrire maintenant", 'Register now', 'Registrati ora')}
                  </Button>
                </p>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <Card className="shadow-xl border-0">
              <CardHeader>
                <CardTitle>{tl('Créer un compte', 'Create an Account', 'Crea un account')}</CardTitle>
                <CardDescription>
                  {tl(
                    'Remplissez le formulaire ci-dessous pour vous inscrire',
                    'Fill out the form below to register',
                    'Compila il modulo sottostante per registrarti'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Google Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mb-4"
                  onClick={() => signInWithGoogle()}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {tl('Continuer avec Google', 'Continue with Google', 'Continua con Google')}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">
                      {tl('ou', 'or', 'o')}
                    </span>
                  </div>
                </div>

                <Form {...registerForm}>
                  <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-4">
                    <FormField
                      control={registerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Email', 'Email', 'Email')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tl(
                                'votre.email@exemple.com',
                                'your.email@example.com',
                                'tua.email@esempio.com'
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={registerForm.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Nom complet', 'Full Name', 'Nome completo')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Mohamed Salah" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={registerForm.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {tl('Numéro de téléphone', 'Phone Number', 'Numero di telefono')}
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="+216 12 345 678" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={registerForm.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{tl('Pays', 'Country', 'Paese')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={tl('Tunisie', 'Tunisia', 'Tunisia')}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={registerForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Adresse', 'Address', 'Indirizzo')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tl('123 Rue Principale', '123 Main St', 'Via Principale 123')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={registerForm.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{tl('Ville', 'City', 'Città')}</FormLabel>
                            <FormControl>
                              <Input placeholder="Hammamet" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={registerForm.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{tl('Code postal', 'Zip Code', 'Codice postale')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={tl('75001', '10001', '00100')}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{tl('Mot de passe', 'Password', 'Password')}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-10 w-10 px-3 py-2"
                                onClick={togglePasswordVisibility}
                              >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={registerForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tl('Confirmer le mot de passe', 'Confirm Password', 'Conferma password')}
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-10 w-10 px-3 py-2"
                                onClick={toggleConfirmPasswordVisibility}
                              >
                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {authError && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertTitle>{tl('Erreur', 'Error', 'Errore')}</AlertTitle>
                        <AlertDescription>{authError}</AlertDescription>
                      </Alert>
                    )}

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {tl('Inscription...', 'Registering...', 'Registrazione...')}
                        </>
                      ) : (
                        tl("S'inscrire", 'Register', 'Registrati')
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
              <CardFooter>
                <p className="text-sm text-center w-full text-muted-foreground">
                  {tl('Vous avez déjà un compte ?', 'Already have an account?', 'Hai già un account?')}{' '}
                  <Button
                    variant="link"
                    className="p-0 h-auto font-normal"
                    onClick={() => setActiveTab('login')}
                  >
                    {tl('Se connecter maintenant', 'Sign in now', 'Accedi ora')}
                  </Button>
                </p>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Auth;
